/**
 * PLN204 v.8 — "Metinė pelno mokesčio deklaracija" (VMI, by 15 June) for a profit-seeking limited-liability
 * company, the main two pages, from the year's books and the accountant's answers. Fields and codes from VMI's
 * template PLN204.mxfd (2026-05-15, FormDefId {B09451AC-AFF5-464B-AF35-057CC01B7699}) and its filling rules
 * (VA-2): amounts in whole euros (49 cents and less dropped), the rate with one decimal. Worked out here:
 * 18 income, 19 profit before tax, 20 non-deductible expenses (appendix PLN204S, by code), 37 = 19 + 20,
 * 40 profit or 41 loss, 43 profit after support (none), 45 tax losses carried forward, 48 = 43 − 45,
 * 52 = 52A = 48, 53A/54A the rate, 55A = 55 = 59 = 62 the tax, 64 = −62 (payable with a minus). Not covered:
 * dividends, securities, foreign tax, reorganisations, investment relief and the other appendices.
 */
export const PLN204_FORM_DEF_ID = '{B09451AC-AFF5-464B-AF35-057CC01B7699}';
/** 53A: 1 standard, 2 small company (PMĮ 5 str. 2 d.), 3 its first tax period (0 %). */
export const PLN204_RATES = [
    { code: 1, label: 'Standartinis tarifas (PMĮ 5 str. 1 d. 1 p.)', rate: (y) => (y >= 2026 ? 17 : y === 2025 ? 16 : 15) },
    { code: 2, label: 'Lengvatinis tarifas mažoms įmonėms (PMĮ 5 str. 2 d.)', rate: (y) => (y >= 2026 ? 7 : y === 2025 ? 6 : 5) },
    { code: 3, label: 'Pirmasis mokestinis laikotarpis (PMĮ 5 str. 2 d., 0 proc.)', rate: () => 0 },
];
/** PLN204S codes most small companies meet (the template lists 36). */
export const PLN204S_CODES = [
    { code: '01', label: 'Netesybos, baudos, delspinigiai ir kitos sankcijos' },
    { code: '04', label: 'Komandiruočių sąnaudos (PMĮ 21 str.)' },
    { code: '05', label: 'Reklamos sumos (PMĮ 22 str.)' },
    { code: '06', label: 'Reprezentacijos sumos (PMĮ 22 str.)' },
    { code: '08', label: 'Mokesčių sumos (PMĮ 24 str.)' },
    { code: '10', label: 'Išlaidos darbuotojų ir jų šeimos narių naudai' },
    { code: '12', label: 'Apskaitoje pripažinta paramos suma' },
    { code: '13', label: 'Dovanos, išskyrus dovanas darbuotojams' },
    { code: '16', label: 'Išmokos, nepagrįstos įrodymais' },
    { code: '29', label: 'Praėjusių laikotarpių klaidų taisymo sąnaudos' },
    { code: '31', label: 'Kitos sąnaudos, laikomos neleidžiamais atskaitymais' },
];
export const wholeEur = (cents) => Math.sign(cents) * Math.floor(Math.abs(cents) / 100 + 0.5);
export function pln204(p) {
    const e18 = wholeEur(p.income), e19 = wholeEur(p.profitBeforeTax);
    const nd = p.nonDeductible.filter((n) => n.amount > 0).map((n) => ({ code: n.code, amount: wholeEur(n.amount) }));
    const e20 = nd.reduce((s, n) => s + n.amount, 0);
    const e37 = e19 + e20;
    const e40 = Math.max(e37, 0), e41 = e37 < 0 ? -e37 : 0;
    const e43 = e40;
    // Earlier losses reduce at most 70 % of the profit (PMĮ 30 str. 4 d.), all of it at the small companies' rate.
    const e45 = Math.min(Math.max(p.lossCarried, 0), p.rateCode === 1 ? Math.floor(e43 * 0.7) : e43);
    const e48 = e43 - e45;
    const rate = PLN204_RATES.find((r) => r.code === p.rateCode).rate(p.year);
    const tax = Math.floor((e48 * rate) / 100 + 0.5);
    const v = (n) => (n === null ? '' : String(n));
    const fields = {
        E4: String(Math.max(0, Math.round(p.employees))),
        E7: '', 'E8-Pradzia-Pabaiga': '', E9: p.answers.e9, E10: p.answers.e10, 'E17-FR0528': p.answers.fr0528, 'E17-FR0438': p.answers.fr0438, E17A: p.answers.e17a, E17B: '', E17C: p.answers.e17c,
        E18: v(e18), E18A: '', E19: v(e19), E20: e20 ? v(e20) : '',
        ...Object.fromEntries(['E21', 'E22', 'E23', 'E24', 'E25', 'E25A', 'E25B', 'E26', 'E27', 'E28', 'E29', 'E30', 'E31', 'E31A', 'E32', 'E33', 'E34', 'E35', 'E36', 'E36A', 'E38'].map((k) => [k, ''])),
        E37: v(e37),
        E39: '', E39A: '', E40: e37 >= 0 ? v(e40) : '', E41: e41 ? v(e41) : '', E42: '', E43: e37 >= 0 ? v(e43) : '', E43A: '', E43B: '', E44: '', E45: e45 ? v(e45) : '', E46: '', E47: '', E47A: '',
        E48: e37 >= 0 ? v(e48) : '', E49: '', E50: '', E51: '', E51A: '',
        E52: e37 >= 0 ? v(e48) : '', E52A: e37 >= 0 ? v(e48) : '', E53A: e37 >= 0 ? String(p.rateCode) : '', E54A: e37 >= 0 ? `${rate},0` : '', E55A: e37 >= 0 ? v(tax) : '',
        E52B: '', E53B: '', E54B: '', E55B: '', E55: e37 >= 0 ? v(tax) : '', E56: '', E57: '', E58: '', E59: e37 >= 0 ? v(tax) : '', E60: '', E61: '', E62: e37 >= 0 ? v(tax) : '',
        E63: '', E64: e37 >= 0 ? v(-tax) : '', E65: '', E65A: '', E66: '', E66A: '', E67: '', E68: '', E69: '', E70: '', E71: '',
    };
    let s = null;
    if (nd.length) {
        s = {};
        for (let i = 1; i <= 32; i++) {
            s[`S1-${i}`] = nd[i - 1]?.code ?? '';
            s[`S2-${i}`] = nd[i - 1] ? String(nd[i - 1].amount) : '';
        }
        s.S3 = String(e20);
    }
    const lines = [['4', Number(fields.E4)], ['18', e18], ['19', e19], ['20', e20], ['37', e37], ['40', e40], ['41', e41], ['45', e45], ['48', e48], ['54A', rate], ['55', tax], ['64', -tax]]
        .map(([box, amount]) => ({ box: String(box), amount: Number(amount) }));
    return { fields, s, lines };
}
//# sourceMappingURL=pln204.js.map